import { endpoint, SERVICE_KEYS, type ServiceKey } from '@shared/api/config';
import { httpRequest } from '@shared/api/http';

/**
 * What the caller may do, service by service — the one input the console gates
 * its own surfaces on.
 *
 * **This is navigation, never authorization.** Both SPAs are public bundles by
 * construction; every read and every write is authorized by the owning backend
 * on the request itself, and nothing here is trusted to protect anything. What
 * it fixes is a different failure: a console that drew all seventeen
 * destinations for every signed-in operator and let the service refuse each one
 * on arrival, so an account without the analytics role met four error cards on
 * the dashboard rather than a console shaped to what it holds.
 *
 * **Roles are per service.** `security-and-auth.md` §4 fixes one role per user
 * per service, with no platform-wide catalog — so this is nine answers, read
 * from the nine services' own `GET /api/v1/me/permissions`, and never from
 * `auth__users.admin_roles`, which is a fan-in hint the owning service is
 * required to re-check. develop has already carried a registry naming a grant
 * the owning service did not hold; a sidebar built on that hint draws sections
 * every read behind them refuses.
 */

/** The two tiers the console distinguishes, plus the default every account holds. */
export type ServiceTier = 'user' | 'operator' | 'admin';

/**
 * One tier per service, or **absent**.
 *
 * Absent means *not known*, and it is deliberately permissive: a service whose
 * read failed leaves its entries visible. Hiding on a network blip turns a
 * transient failure into a console missing half its navigation, while showing an
 * entry the backend then refuses is exactly the behaviour that shipped before
 * this file — no worse, and self-correcting on the next bootstrap. Only a
 * *successful* answer of `user` hides anything.
 */
export type CapabilityMap = Partial<Record<ServiceKey, ServiceTier>>;

const TIER_ORDER: Readonly<Record<ServiceTier, number>> = { user: 0, operator: 1, admin: 2 };

const TIERS: readonly ServiceTier[] = ['user', 'operator', 'admin'];

/**
 * The nine services do not agree on where they put the role name.
 *
 * Five shapes are in production, measured against develop:
 *
 *     auth          {"role_name": "admin", "permissions": [...]}
 *     analytics     {"name": "user", "permissions": [...]}
 *     content       {"role": {"name": "user", "permissions": []}}
 *     support       {"role": {"name": "user", "isDefault": true, ...}}
 *     notification  {"role": {"name": "user", "permissions": {...}}}
 *
 * Unifying them is a nine-service payload change with its own consumers. Reading
 * all five here costs one function and stops the console from being the place
 * that divergence breaks. The keys are tried in order of specificity, so a
 * service that later adds a wrapper does not start resolving to the wrong field.
 */
export function readRoleName(payload: unknown): ServiceTier | undefined {
  const root = asRecord(payload);
  if (!root) return undefined;

  const nested = asRecord(root.role);
  const candidate =
    pickString(nested, 'name') ??
    pickString(nested, 'role_name') ??
    pickString(root, 'role_name') ??
    pickString(root, 'roleName') ??
    pickString(root, 'name');

  return toTier(candidate);
}

/**
 * A name this bundle does not know resolves to **nothing**, not to a guess.
 *
 * All nine now seed from `user` · `operator` · `admin` — `content` seeded a
 * fourth, `operator_editorial_reviewer`, until it was retired — so an unknown
 * name is a role somebody added on the service side. Mapping it to `user` would
 * hide sections from an account that holds more than `user`; leaving it unknown
 * leaves those entries visible and lets the backend answer.
 */
function toTier(name: string | undefined): ServiceTier | undefined {
  return name !== undefined && TIERS.includes(name as ServiceTier) ? (name as ServiceTier) : undefined;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : undefined;
}

function pickString(source: Record<string, unknown> | undefined, key: string): string | undefined {
  const value = source?.[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * Read all nine at once, and never throw.
 *
 * `allSettled` rather than `all`: one refusing service must not cost the console
 * the other eight, and a rejection is the *unknown* state rather than a denial.
 * The calls are unauthenticated-looking but are not — `httpRequest` carries the
 * bearer this bootstrap has just obtained, and `/me/permissions` is about the
 * caller, so it needs one.
 */
export async function loadCapabilities(accessToken: string | null): Promise<CapabilityMap> {
  const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined;

  const answers = await Promise.allSettled(
    SERVICE_KEYS.map(async (service) => {
      const payload = await httpRequest<unknown>(endpoint(service, '/me/permissions'), {
        method: 'GET',
        ...(headers ? { headers } : {}),
      });
      return [service, readRoleName(payload)] as const;
    }),
  );

  const capabilities: CapabilityMap = {};
  for (const answer of answers) {
    if (answer.status !== 'fulfilled') continue;
    const [service, tier] = answer.value;
    if (tier !== undefined) capabilities[service] = tier;
  }
  return capabilities;
}

/**
 * Whether the caller holds at least `tier` in `service`.
 *
 * `'*'` asks the same question of **any** service, which is what a surface
 * fanning out over all nine needs: the runtime-configuration page is worth
 * drawing for an account that administers one service, and it hides the eight
 * sections that account cannot read rather than the whole page.
 */
export function holdsTier(
  capabilities: CapabilityMap,
  service: ServiceKey | '*',
  tier: ServiceTier,
): boolean {
  if (service === '*') return SERVICE_KEYS.some((key) => holdsTier(capabilities, key, tier));

  const held = capabilities[service];
  return held === undefined || TIER_ORDER[held] >= TIER_ORDER[tier];
}
